import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { createBreadcrumbSchema } from '../lib/structured-data';
import { Shield, Mail, Clock, CheckCircle2 } from 'lucide-react';

export function VulnerabilityDisclosure() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Vulnerability Disclosure Policy"
          description="How to report security vulnerabilities in Situs AI products. Our commitment to good-faith researchers and safe harbor provisions."
          canonical="https://situsai.ca/vulnerability-disclosure"
        />
        <JsonLd data={createBreadcrumbSchema('/vulnerability-disclosure')} />

        {/* Hero */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <Shield className="w-4 h-4" /> Security
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Vulnerability Disclosure Policy
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-3xl mx-auto">
            We believe in working with the security community to keep our platform safe. This policy outlines how we handle vulnerability reports.
          </p>
          <p className="text-sm text-[var(--color-csai-muted)] mt-4">Last Updated: April 17, 2026</p>
        </section>

        {/* Scope */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-4">Scope</h2>
          <p className="text-[var(--color-csai-muted)] mb-4">This policy applies to all Situs AI products and services:</p>
          <ul className="list-disc list-inside text-[var(--color-csai-muted)] space-y-1">
            <li>situsai.ca and all subdomains</li>
            <li>Orca AI platform (app.situsai.ca)</li>
            <li>API endpoints (api.situsai.ca)</li>
          </ul>
        </section>

        {/* Reporting */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-4">Reporting a Vulnerability</h2>
          <p className="text-[var(--color-csai-muted)] mb-6">
            If you believe you have discovered a security vulnerability, please report it to us at:
          </p>
          <div className="bg-[var(--color-csai-surface)] rounded-xl p-6 flex items-center gap-3 mb-6">
            <Mail className="w-5 h-5 text-[var(--color-csai-primary)]" />
            <a href="mailto:security@situsai.ca" className="text-[var(--color-csai-primary)] hover:underline font-medium">
              security@situsai.ca
            </a>
          </div>
          <p className="text-[var(--color-csai-muted)] mb-4">Please include in your report:</p>
          <ol className="list-decimal list-inside text-[var(--color-csai-muted)] space-y-2">
            <li>Description of the vulnerability</li>
            <li>Steps to reproduce the issue</li>
            <li>Potential impact of the vulnerability</li>
            <li>Any proof-of-concept or screenshots (optional)</li>
          </ol>
        </section>

        {/* Our Commitment */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-6">Our Commitment</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-6 border border-[var(--color-csai-surface)] flex items-start gap-3">
              <Clock className="w-5 h-5 text-[var(--color-csai-primary)] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--color-csai-accent)]">48-Hour Acknowledgement</p>
                <p className="text-sm text-[var(--color-csai-muted)]">We will acknowledge receipt of your report within 48 hours</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[var(--color-csai-surface)] flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[var(--color-csai-primary)] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--color-csai-accent)]">5-Day Initial Assessment</p>
                <p className="text-sm text-[var(--color-csai-muted)]">We will provide an initial assessment within 5 business days</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[var(--color-csai-surface)] flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[var(--color-csai-primary)] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--color-csai-accent)]">Regular Updates</p>
                <p className="text-sm text-[var(--color-csai-muted)]">We will keep you informed of remediation progress</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-[var(--color-csai-surface)] flex items-start gap-3">
              <Shield className="w-5 h-5 text-[var(--color-csai-primary)] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--color-csai-accent)]">No Retaliation</p>
                <p className="text-sm text-[var(--color-csai-muted)]">We will not pursue legal action against good-faith researchers</p>
              </div>
            </div>
          </div>
        </section>

        {/* Guidelines */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-4">Guidelines</h2>
          <ul className="space-y-3 text-[var(--color-csai-muted)]">
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
              Only test against your own accounts and data
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
              Do not access, modify, or delete other users' data
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
              Do not degrade service availability (no DDoS or spam testing)
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
              Report vulnerabilities promptly — do not disclose publicly until we have remediated
            </li>
            <li className="flex items-start gap-3">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 shrink-0"></span>
              Use the provided contact channels only
            </li>
          </ul>
        </section>

        {/* Safe Harbor */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-4">Safe Harbor</h2>
          <p className="text-[var(--color-csai-muted)]">
            We consider vulnerability research conducted in accordance with this policy to be authorized and permitted. We will not initiate legal action against researchers who follow these guidelines. If legal action is initiated by a third party against you for activities conducted under this policy, we will take steps to make known our authorization of your work.
          </p>
        </section>

        {/* Back */}
        <section className="pb-16 text-center px-4">
          <a href="/security" className="text-[var(--color-csai-primary)] hover:underline text-sm">
            &larr; Back to Security page
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
}
